'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    id: 'faq-001',
    q: 'What is Richésse and how does it work?',
    a: 'Richésse is a premium lifestyle brand that sells wellness, beauty, and personal care products through a network of independent members. When you join, you receive your own personalized storefront (richesse.global/yourname) that you can share with customers. When customers purchase through your store, those sales are attributed to you.',
  },
  {
    id: 'faq-002',
    q: 'How do I get my personal Richésse store?',
    a: 'Your personal storefront is created automatically the moment you complete your Richésse membership registration. You choose your unique username, and your store at richesse.global/username is immediately live and ready to share — no technical setup required.',
  },
  {
    id: 'faq-003',
    q: 'What is Personal Volume (PV) and why does it matter?',
    a: 'Personal Volume (PV) is the qualifying value assigned to each product. PV is used to determine your eligibility for different commission tiers and bonuses. Each product has a designated PV value, and your monthly PV total from personal and customer sales determines your commission level.',
  },
  {
    id: 'faq-004',
    q: 'Is there a cost to become a Richésse member?',
    a: 'Richésse offers a standard membership that gives you access to member pricing, your personal storefront, and the Academy. There is no mandatory product purchase to join. Optional starter packages are available for those who want to begin with a product collection.',
  },
  {
    id: 'faq-005',
    q: 'Can I earn income with Richésse?',
    a: 'Richésse members have the opportunity to earn through personal customer sales and team volume. Income is not guaranteed and results vary significantly based on individual effort, skills, and market conditions. Please review our Income Disclosure Statement for representative earnings data before making any business decisions.',
  },
  {
    id: 'faq-006',
    q: 'Are Richésse products safe and clinically tested?',
    a: 'All Richésse products are developed with rigorous quality standards and comply with applicable regulatory requirements in the markets we operate. Our wellness products are not intended to diagnose, treat, cure, or prevent any disease. Please consult a healthcare professional before starting any supplement regimen.',
  },
  {
    id: 'faq-007',
    q: 'How does the referral and sponsor system work?',
    a: 'When you join Richésse, you are assigned a sponsor — the person whose referral link or code you used to register. Your sponsor can provide guidance and support. You earn commissions based on your own sales and the collective volume of your team, as defined in the current Compensation Plan.',
  },
  {
    id: 'faq-008',
    q: 'What is the Richésse Academy?',
    a: 'The Richésse Academy is a built-in learning platform covering product education, wellness science, digital marketing, personal branding, social media, and business fundamentals. All members have access to Academy courses as part of their membership.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>('faq-001');

  const toggle = (id: string) => setOpenId(prev => prev === id ? null : id);

  return (
    <section className="py-24 lg:py-32 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="section-divider" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">FAQ</span>
            </div>
            <h2 className="font-display text-section-title text-foreground mb-4">
              Common Questions
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Everything you need to know about Richésse, membership, products, and the opportunity.
            </p>
            <div className="card-elevated p-6 rounded-xl">
              <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-primary mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">Still have questions?</p>
              <p className="text-xs text-muted-foreground mb-4">Our team is available to help you make an informed decision.</p>
              <button className="btn-primary w-full py-2.5 rounded-lg text-sm font-semibold">
                Contact Support
              </button>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-3 space-y-3">
            {faqs.map(faq => (
              <div key={faq.id} className="card-elevated rounded-xl overflow-hidden">
                <button
                  onClick={() => toggle(faq.id)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground pr-4">{faq.q}</span>
                  <Icon
                    name="ChevronDownIcon"
                    size={18}
                    className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${openId === faq.id ? 'rotate-180' : ''}`}
                  />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openId === faq.id ? 'max-h-80' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}